/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  ComponentItemFinderDialogConfig,
  componentItemFinderDialogRoute,
  ComponentItemFinderListItem,
} from '@gh/builder-shared';
import { ComponentItemFilterMode } from '@gh/builder-shared-data';
import {
  FormState,
  FormStateBuilder,
  getRouteInitData,
  getSingleNewSelectedEntity,
} from '@gh/common-shared';
import { emptySelectionModel, SelectionModel, SlicedDataSource } from '@gh/core-data';
import {
  DialogService,
  NotificationService,
  setControlValue,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { of$ } from '@gh/rx';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { BulkUpdateBusinessService } from '../../resources/bulk-update-business.service';
import {
  BulkUpdateComponentReplace,
  ComponentBulkUpdateListItem,
  getBulkUpdateItemModelFromFinder,
} from '../../resources/bulk-update.model';
import { BulkUpdateAbstractComponent } from '../bulk-update-abstract.component';
import { ComponentGridDefinition } from '../component-grid-definition.service';

@Component({
  standalone: false,
  selector: 'gh-bulk-update-component-replace',
  templateUrl: 'bulk-update-component-replace.component.html',
  styleUrls: ['bulk-update-component-replace.component.scss'],
  providers: [ComponentGridDefinition],
})
export class BulkUpdateComponentReplaceComponent extends BulkUpdateAbstractComponent implements OnInit {

  componentDataSource: SlicedDataSource<ComponentBulkUpdateListItem>;
  targetSelectionModel = emptySelectionModel();
  form: FormState<any>;
  dirty: boolean = false;

  constructor(public componentGridDefinition: ComponentGridDefinition,
              private notificationService: NotificationService,
              private dialogService: DialogService,
              private waitingService: WaitingService,
              private route: ActivatedRoute,
              private businessService: BulkUpdateBusinessService,
              private formStateBuilder: FormStateBuilder) {
    super();
  }

  get itemFrom(): ComponentItemFinderListItem {
    return this.form.group.get('from')!.value;
  }

  get itemTo(): ComponentItemFinderListItem {
    return this.form.group.get('to')!.value;
  }

  get allowUpdate(): boolean {
    return this.form.group.get('allowUpdate')!.value;
  }

  ngOnInit(): void {
    const { initData } = getRouteInitData(this.route);
    this.form = this.formStateBuilder.buildPlain({
      children: {
        from: void 0,
        to: void 0,
        allowUpdate: { enabled: initData.allowUpdate },
      },
    });
    this.form.valueChanges.subscribe(() => this.resetDirty(true));
    this.load();
  }

  onTargetSelectionChanged(model: SelectionModel): void {
    this.resetDirty(true);
    this.targetSelectionModel = model;
  }

  load(): void {
    this.componentDataSource = this.businessService.loadComponentGridDataSource();
  }

  onSave(): void {
    this.saveAction();
  }

  onSearchFromElement(): void {
    this.dialogService.openWithInput(componentItemFinderDialogRoute, <ComponentItemFinderDialogConfig>{
      multiselection: false,
      disableFilterMode: true,
      filterMode: ComponentItemFilterMode.Items,
      preselected: [],
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getSingleNewSelectedEntity))
      .subscribe((item: ComponentItemFinderListItem) => setControlValue(this.form.group, 'from', item));
  }

  onSearchToElement(): void {
    this.dialogService.openWithInput(componentItemFinderDialogRoute, <ComponentItemFinderDialogConfig>{
      multiselection: false,
      filterMode: ComponentItemFilterMode.Items,
      disableFilterMode: true,
      preselected: [],
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getSingleNewSelectedEntity))
      .subscribe((item: ComponentItemFinderListItem) => setControlValue(this.form.group, 'to', item));
  }

  protected afterSave(): void {
    this.notificationService.success('msg_bulk_update_initiated_successfully');
    this.targetSelectionModel = emptySelectionModel();
    this.form.group.reset();
    this.resetDirty(false);
  }

  protected getRequestBody(): BulkUpdateComponentReplace {
    return {
      targetComponents: this.targetSelectionModel,
      oldEntity: getBulkUpdateItemModelFromFinder(this.itemFrom),
      newEntity: getBulkUpdateItemModelFromFinder(this.itemTo),
      allowUpdate: this.allowUpdate,
    };
  }

  protected saveAction(): void {
    if (this.form.group.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.confirmation().pipe(
      switchMap$(() =>
        this.businessService.replaceComponentSave(this.getRequestBody()).pipe(
          (this.waitingService.operator<any>()))))
      .subscribe(
        () => this.afterSave(),
        this.notificationService.processAll,
      );
  }

  private confirmation(): Observable<boolean> {
    if ((this.itemFrom.uomId !== this.itemTo.uomId) && this.allowUpdate) {
      return this.dialogService.confirmation(yesNoConfirmation('msg_bu_replace_not_same_uom_allow_update')).pipe(
        filter$(yesAnswer),
        map$(Boolean));
    } else {
      return of$(true);
    }
  }

}
